import { useEffect, useState } from "react";
import type { MetricSnapshot, StreamState } from "./types";

const MAX_HISTORY = 900;

function appendHistory(history: MetricSnapshot[], snapshot: MetricSnapshot) {
  if (history.at(-1)?.id === snapshot.id) return history;
  return [...history, snapshot].slice(-MAX_HISTORY);
}

export function useStatusStream(paused: boolean) {
  const [snapshot, setSnapshot] = useState<MetricSnapshot | null>(null);
  const [history, setHistory] = useState<MetricSnapshot[]>([]);
  const [streamState, setStreamState] = useState<StreamState>("connecting");

  useEffect(() => {
    let cancelled = false;
    fetch("/api/status")
      .then((response) => response.json())
      .then((data: MetricSnapshot) => {
        if (cancelled) return;
        setSnapshot(data);
        setHistory((current) => appendHistory(current, data));
      })
      .catch(() => {
        if (!cancelled) setStreamState("error");
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (paused) {
      setStreamState("paused");
      return undefined;
    }

    setStreamState("connecting");
    const source = new EventSource("/api/stream");

    source.addEventListener("open", () => {
      setStreamState("live");
    });

    source.addEventListener("snapshot", (event) => {
      const data = JSON.parse((event as MessageEvent).data) as MetricSnapshot;
      setSnapshot(data);
      setHistory((current) => appendHistory(current, data));
      setStreamState("live");
    });

    source.addEventListener("error", () => {
      setStreamState("error");
    });

    return () => {
      source.close();
    };
  }, [paused]);

  return { snapshot, history, streamState };
}
